/// <amd-module name="Build/DesignerExtensions/YamlSnippetForm/Components/AzureKubernetesEndpointInput" />

import { ServiceEndpointDetails } from "Build/DesignerExtensions/YamlAssistantLibrary/Contracts/Types";
import { DataSourcePickList } from "Build/DesignerExtensions/YamlSnippetForm/Components/DataSourcePickList";
import { SnippetFormSource } from "Build/DesignerExtensions/YamlSnippetForm/Sources/SnippetFormSource";
import { EndpointConstants, EndpointHelper, Subscription } from "Build/DesignerExtensions/YamlSnippetForm/Utilities/EndpointHelper";
import { YamlSnippetInput } from "Pipelines/Common/Library/Contracts/DataProvider";
import * as React from "react";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { FormItem } from "VSSUI/FormItem";
import { RadioButton, RadioButtonGroup, RadioButtonGroupDirection } from "VSSUI/RadioButton";
import { TextField } from "VSSUI/TextField";

export interface KubernetesClusterInfo {
    clusterId: string;
    namespace: string;
    fqdn: string;
}

interface Props {
    subscription?: Subscription;
    endpointToken?: string
    className?: string;
    clusterAdmin?: boolean;
    onAllValuesReceived: (serviceEndpointObject: any) => void;
    onError(location: string, error: Error): void;
}

interface State {
    clusterId: string;
    namespace: string;
    fqdn: string;
    createNewNamespace: string;
    clusterName: string;
    clusterAdmin: boolean;
}

export class AzureKubernetesEndpointInput extends VssComponent<Props, State, IVssComponentContext> {
    private _source: SnippetFormSource;
    private _clusterAdmin: boolean;

    constructor(props: Props, context: IVssComponentContext) {
        super(props, context);
        const state: State = {
            clusterId: "",
            namespace: "",
            fqdn: "",
            createNewNamespace: "false",
            clusterName: "",
            clusterAdmin: false
        }
        this.state = state;
        this._source = new SnippetFormSource(this.context.pageContext);
    }

    public render(): JSX.Element {
        const clusterInputDefinition = {
            dataSourceBinding: {
                dataSourceName: "AzureKubernetesClusters",
                resultTemplate: `{ "Value": "{'id':'{{id}}', 'fqdn': '{{properties.fqdn}}', 'name': '{{name}}' }", "DisplayValue" : "{{name}}" }`,
                parameters: {}
            },
            dataSourceDependency: "subscriptionId",
            label: "Cluster",
            name: "clusterName"
        } as YamlSnippetInput;

        const namespaceInputDefintion = {
            dataSourceBinding: {
                dataSourceName: "KubernetesNamespaces",
                resultTemplate: `{ "Value" : "{{metadata.name}}", "DisplayValue" : "{{metadata.name}}" }`,
                parameters: {}
            },
            dataSourceDependency: "clusterName",
            label: "",
            name: "namespace"
        } as YamlSnippetInput;

        if (this.props.clusterAdmin != undefined && this.props.clusterAdmin != this._clusterAdmin) {
            this._clusterAdmin = this.props.clusterAdmin;
            this.onValueChanged("namespace", this.state.namespace); // To trigger update of the newly created kubernetes object
        }

        const { className } = this.props;

        return <div className="section">
            <div className={className}>
                <DataSourcePickList
                    inputDefinition={clusterInputDefinition}
                    serviceConnectionProvider={this._source}
                    onValueChanged={this.onValueChanged}
                    getValue={this.getValue}
                    onError={this.props.onError}
                    preloadItems={true}
                    serviceEndpointDetails={EndpointHelper.getAzureRMServiceEndpointDetails(this.context.pageContext, this.props.subscription, this.props.endpointToken)}
                    makeRecursiveDatasourceCalls={true} />
            </div>
            <div className={className}>
                <label className={"bolt-textfield-label"}>Namespace</label>
                <RadioButtonGroup
                    className=""
                    selectedButtonId={this.getValue("createNewNamespace")}
                    onSelect={(value) => this.onValueChanged("createNewNamespace", value)}
                    direction={RadioButtonGroupDirection.Horizontal}>
                    {this._getRadioButtons()}
                </RadioButtonGroup>
                {this.state.createNewNamespace === "false" &&
                    <DataSourcePickList
                        inputDefinition={namespaceInputDefintion}
                        serviceConnectionProvider={this._source}
                        onValueChanged={this.onValueChanged}
                        getValue={this.getValue}
                        onError={this.props.onError}
                        preloadItems={!!this.state.clusterId}
                        serviceEndpointDetails={this._getKubernetesServiceEndpointDetails()} />
                }
                {this.state.createNewNamespace === "true" &&
                    <FormItem className="flex-row" label={""}>
                        <TextField
                            containerClassName="namespace-text-field"
                            value={this.getValue("namespace")}
                            onChange={(e, value) => this.onValueChanged("namespace", value)}
                        />
                    </FormItem>
                }
            </div>
        </div>
    }

    public componentDidMount() {
        super.componentDidMount();
    }

    private onValueChanged = (name: string, value: string) => {
        if (name === "clusterName") {
            const cluster = JSON.parse(value.replace(/'/g, '"')); // The replace logic is required to convert the result template's value to a valid JSON
            this.setState({
                clusterId: cluster["id"],
                fqdn: `https://${cluster["fqdn"]}`,
                clusterName: cluster["name"],
                namespace: ""
            });
        }
        else if (name === "namespace") {
            this.setState({
                ...this.state,
                namespace: value.toLowerCase()
            }, () => {
                this.props.onAllValuesReceived(this._createServiceEndpointObject());
            });
        }
        else if (name === "createNewNamespace") {
            this.setState({
                ...this.state,
                createNewNamespace: value,
                namespace: ""
            });
        }
    }

    private getValue = (name: string) => {
        switch (name) {
            case "createNewNamespace":
                return this.state.createNewNamespace;
            case "namespace":
                return this.state.namespace;
            case "subscriptionId":
                return !!this.props.subscription && !!this.props.subscription.id ? "0" : "";
            case "clusterName":
                return !!this.state.clusterName ? this.state.clusterName : "";
            default:
                return "";
        }
    }

    private _getKubernetesServiceEndpointDetails = () => {
        // Creating service endpoint details for kubernetes auth scheme
        if (this.props.subscription) {
            let serviceEndpointDetails: ServiceEndpointDetails | undefined = undefined;
            const endpointAuthorization = {
                parameters: {
                    azureEnvironment: EndpointConstants.azureEnvironment,
                    azureTenantId: this.props.subscription.subscriptionAccount.subscriptionTenantId,
                },
                scheme: EndpointConstants.AuthSchemes.kubernetes
            }
            serviceEndpointDetails = {
                authorization: endpointAuthorization,
                data: {
                    authorizationType: "AzureSubscription",
                    azureSubscriptionId: this.props.subscription.id,
                    azureSubscriptionName: this.props.subscription.displayName,
                    clusterId: this.state.clusterId
                },
                type: EndpointConstants.EndpointTypes.kubernetes,
                url: this.state.fqdn
            }

            return serviceEndpointDetails;
        }
    }

    private _createServiceEndpointObject = () => {
        if (this.props.subscription) {
            const parameters: { [key: string] : string} = {
                "azureEnvironment": "AzureCloud",
                "azureTenantId": this.props.subscription.subscriptionAccount.subscriptionTenantId
            };
            if(this.props.endpointToken) {
                parameters["azureAccessToken"] = this.props.endpointToken;
            }
            return {
                "data": {
                    "authorizationType": "AzureSubscription",
                    "azureSubscriptionId": this.props.subscription.id,
                    "azureSubscriptionName": this.props.subscription.displayName,
                    "clusterId": this.state.clusterId,
                    "namespace": this.state.namespace,
                    "operation.createOrReuseNamespace": this.state.createNewNamespace,
                    "clusterAdmin": this.props.clusterAdmin ? "true" : "false"
                },
                "name": `${this.state.clusterName}-${this.state.namespace}`,
                "type": "kubernetes",
                "url": this.state.fqdn,
                "authorization": {
                    "scheme": "Kubernetes",
                    "parameters": parameters
                }
            };
        }
    }

    private _getRadioButtons = () => {
        return [
            <RadioButton key="true" className="namespace-radio-button" id={"true"} text={"New"} />,
            <RadioButton key="false" className="namespace-radio-button" id={"false"} text={"Existing"} />
        ];
    }
}
